import { Injectable } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonIntegratedArchitectureFrameworkService } from "@org-common/lib/architecture/common-integrated-architecture-framework.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { lastValueFrom } from "rxjs";
import { AuthorisationService } from "../authorisation/authorisation.service";

@Injectable({
    providedIn: "root",
})
export class CommonIntegratedArchitectureFrameworkAuthService {
    public static ReadTier2 = "readTier2Architecture";
    public static EditTier2 = "editTier2Architecture";
    public static ConfigureTier2 = "configureTier2Architecture";
    public static EditAnyTier2 = "editAnyTier2Architecture";
    public static ConfigureAnyTier2 = "configureAnyTier2Architecture";
    public static EditRoleContents = "editRoleContents";

    public constructor(
        private authorisationService: AuthorisationService,
        private commonTeamsAuthService: CommonTeamsAuthService,
        private commonIntegratedArchitectureFrameworkService: CommonIntegratedArchitectureFrameworkService,
    ) {}

    public static registerAccessVerifiers(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.ReadTier2,
            {
                requirePermissions: [
                    FeaturePermissionName.ArchitectureTier2Read,
                    FeaturePermissionName.ArchitectureTier2Edit,
                    FeaturePermissionName.ArchitectureTier2Configure,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.EditAnyTier2,
            {
                requirePermissions: FeaturePermissionName.ArchitectureTier2Edit,
            },
        );
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.ConfigureAnyTier2,
            {
                requirePermissions: FeaturePermissionName.ArchitectureTier2Configure,
            },
        );
        authorisationService.registerAccessVerifier(
            CommonIntegratedArchitectureFrameworkAuthService.EditRoleContents,
            {
                requirePermissions: [
                    FeaturePermissionName.ArchitectureTier2PersonalEdit,
                ],
            },
        );
    }

    public currentPersonCan(checker: (person: Person) => boolean) {
        if (!this.authorisationService.currentPerson) {
            return false;
        }

        return checker(this.authorisationService.currentPerson);
    }

    @Autobind
    public currentPersonCanEntity<TEntity>(checker: (person: Person, entity?: TEntity) => Promise<boolean> | boolean, entity?: TEntity) {
        if (!this.authorisationService.currentPerson) {
            return false;
        }

        return checker(this.authorisationService.currentPerson, entity);
    }

    @Autobind
    public personCanReadTier2(person: Person) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier2Read)
            || this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier2Edit)
            || this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier2Configure);
    }

    public currentPersonCanReadTier2() {
        if (!this.authorisationService.currentPerson) {
            return false;
        }

        return this.personCanReadTier2(this.authorisationService.currentPerson);
    }

    public async canEditRoleContents(role: Role) {
        // currentPersonCanEntity has return type of boolean | Promise -> so declare this function as async to let both wrapped to a promise
        return this.currentPersonCanEntity<Role>(this.personCanEditRoleContents, role);
    }

    public async canEditRoleByTeam(role: Role) {
        // currentPersonCanEntity has return type of boolean | Promise -> so declare this function as async to let both wrapped to a promise
        return this.currentPersonCanEntity<Role>(this.personCanEditRoleByTeam, role);
    }

    @Autobind
    public async personCanEditRoleContents(person: Person, role?: Role) {
        const canEditTier2 = this.personCanEditTier2(person);
        const canEditTier1 = this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier1Edit);
        const isAccessManager = this.authorisationService.personHasPermission(person, FeaturePermissionName.OrganisationAccessManagementConfigure);
        if (canEditTier2 || canEditTier1 || isAccessManager) {
            return true;
        } else if (role) {
            if (await this.personCanEditRoleByTeam(person, role)) {
                return true;
            }

            const canEditOwnRole = this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier2PersonalEdit);
            if (canEditOwnRole) {
                return !!role.roleConnections.find((rc) => rc.connectionId === person.getLatestConnection()?.connectionId);
            }
        }

        return false;
    }

    @Autobind
    public async personCanEditRoleByTeam(person: Person, role: Role) {
        // make sure we have team roles primed
        await lastValueFrom(this.commonIntegratedArchitectureFrameworkService.getTeamRoleLocationsForRole(role.roleId));

        return role.roleLocations.some((rl) => this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier2Edit, rl)
            || (rl.team && this.commonTeamsAuthService.currentPersonCanEditTeam(rl.team)));
    }

    @Autobind
    public personCanEditTier2(person: Person) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier2Edit);
    }

    @Autobind
    public personCanConfigureTier2(person: Person, team?: Team) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.ArchitectureTier2Configure, team);
    }
}
